import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, TrendingUp, Zap } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useSettings } from '../../context/SettingsContext';

const Header = () => {
  const { settings } = useSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Build nav from settings (visible categories) + pinned Vizyon Sektör
  const visibleCategories = (settings.categories || []).filter((c) => c.visible);
  const navItems = [
    ...visibleCategories.map((c) => ({ ...c, featured: false })),
    { slug: 'vizyon-sektor', label: 'Vizyon Sektör', featured: true },
  ];

  // Parse site title into two lines (first word = HABER, rest = tagline)
  const [mainWord, ...rest] = (settings.site_title || "Haber Türkiye'den").split(' ');
  const tagline = settings.site_tagline || rest.join(' ');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Breaking News Bar */}
      <div className="bg-red-600 text-white py-1.5">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Zap className="w-4 h-4 fill-current animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-wider">SON DAKİKA</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="text-xs font-medium truncate">
              Merkez Bankası faiz kararını açıkladı: Politika faizi sabit tutuldu | BIST 100 yeni rekor seviyede
            </p>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-16 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              {settings.logo_data ? (
                <img
                  src={settings.logo_data}
                  alt={settings.site_title}
                  className="h-10 md:h-12 w-auto object-contain"
                />
              ) : (
                <div className="flex flex-col">
                  <span className="text-xl md:text-2xl font-black tracking-tight text-[#0F172A] leading-none uppercase">
                    {mainWord}
                  </span>
                  {tagline && (
                    <span className="text-[10px] font-bold text-red-600 tracking-[0.2em] uppercase">
                      {tagline}
                    </span>
                  )}
                </div>
              )}
            </Link>

            {/* Desktop Search */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Input
                  type="text"
                  placeholder="Haberlerde ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-10 h-10 text-sm bg-gray-50 border-gray-200 focus:bg-white focus:border-red-500 focus:ring-red-500 rounded-full"
                  data-testid="desktop-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
                  data-testid="desktop-search-btn"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              <Link to="/vizyon-sektor" className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full text-sm font-bold hover:from-red-700 hover:to-red-800 transition-all">
                <TrendingUp className="w-4 h-4" />
                VİZYON SEKTÖR
              </Link>

              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSearchOpen(!searchOpen)} data-testid="mobile-search-toggle">
                <Search className="w-5 h-5" />
              </Button>

              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="hidden lg:block bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center">
            {navItems.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'vizyon-sektor' ? '/vizyon-sektor' : `/kategori/${cat.slug}`}
                className={`flex items-center gap-1 px-4 py-3 text-sm font-semibold uppercase tracking-wide transition-colors ${
                  cat.featured
                    ? 'text-yellow-400 hover:text-yellow-300'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-${cat.slug}`}
              >
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Search */}
      {searchOpen && (
        <div className="md:hidden py-3 px-4 bg-gray-50 border-b">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              type="text"
              placeholder="Haberlerde ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 rounded-full"
              autoFocus
              data-testid="mobile-search-input"
            />
            <Button type="submit" size="sm" className="rounded-full bg-red-600 hover:bg-red-700" data-testid="mobile-search-btn">
              <Search className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col gap-1">
              {navItems.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'vizyon-sektor' ? '/vizyon-sektor' : `/kategori/${cat.slug}`}
                  className={`px-4 py-3 text-sm font-semibold uppercase tracking-wide rounded-lg transition-colors ${
                    cat.featured ? 'text-white bg-red-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
